import { createNoise2D } from "simplex-noise";

const elevations = [];
const aggradation = [];
const erosion = [];
window.elevations = elevations;
window.aggradation = aggradation;
window.erosion = erosion;

class TerrainGenerator {
  constructor(prng) {
    this.prng = prng;
    this.noise2D = createNoise2D(prng);
    this.riverPoints = null;

    this.frequency = 1 / 400;
    // low elevation terraces
    this.stepWidth = 0.4;
    this.numSteps = 10;
  }

  p(n) {
    return 1 + (this.prng() - 0.5) / n;
  }

  width(x, y) {
    // [3, 10]
    return Math.max(3, 2 + this.noise2D(x / 200, y / 200) * 8);
  }

  setRiverPoints(x, y) {
    this.riverPoints = { x, y };
    this.minX = Math.min(...x);
    this.maxX = Math.max(...x);
  }

  // Get the minimum distance from a point to the river
  getRiverDistance(x, y) {
    if (!this.riverPoints) return Infinity;

    let minDist = Infinity;
    for (let i = 0; i < this.riverPoints.x.length; i++) {
      const dx = x - this.riverPoints.x[i];
      const dy = y - this.riverPoints.y[i];
      const dist = Math.sqrt(dx * dx + dy * dy);
      minDist = Math.min(minDist, dist);
    }
    return minDist;
  }

  terrace(x, y) {
    const progression = 1 - (x - this.minX) / (this.maxX - this.minX);
    const combined =
      progression +
      this.noise2D(-x * this.frequency * 1.3, -y * this.frequency * 1.3) *
        this.stepWidth;

    const value = Math.floor(combined * this.numSteps) / this.numSteps;

    // 1/5 because we apply this when elevation < 0.2 (=1/5)
    return Math.max(0, Math.min(1, value)) / 5;
  }

  // Get elevation at a point
  getElevation(x, y) {
    const a = Math.floor(x);
    const b = Math.floor(y);

    if (elevations[a] && typeof elevations[a][b] === "number") {
      return elevations[a][b];
    }

    // Generate base terrain using multiple octaves of noise
    let elevation = 0;
    let amplitude = 1.0;
    let freq = this.frequency;

    for (let i = 0; i < 4; i++) {
      elevation += amplitude * this.noise2D(x * freq, y * freq);
      amplitude *= 0.5;
      freq *= 2;
    }

    // Scale elevation to 0-1 range
    elevation = (elevation + 1) * 0.5;

    const dist = this.getRiverDistance(x, y);
    const D = 400;
    const sqD = Math.sqrt(D);
    if (dist < D) {
      elevation = Math.max(0, elevation - (1 - Math.sqrt(dist) / sqD));
    }
    if (elevation < 0.2) {
      elevation = this.terrace(x, y) - (1 - Math.sqrt(dist) / sqD) / 10;
      // console.log(1 - Math.sqrt(dist) / sqD);
    }

    if (!Array.isArray(elevations[a])) {
      elevations[a] = [];
      aggradation[a] = [];
      erosion[a] = [];
    }

    elevations[a][b] = elevation;
    erosion[a][b] = 0;
    aggradation[a][b] = 0;
    return elevation;
  }

  deposit(x, y) {
    const a = Math.floor(x);
    const b = Math.floor(y);
    if (!aggradation[a]) aggradation[a] = Array(b).fill(0);
    if (aggradation[a] && typeof aggradation[a][b] === "number") {
      aggradation[a][b]++;
      // if (elevations[a] && typeof elevations[a][b] === "number") {
      //   elevations[a][b] += 0.01;
      // }
      if (this.getErosion(a, b) > 0) erosion[a][b]--;
    }
  }

  erode(x, y, factor = 1) {
    const a = Math.floor(x);
    const b = Math.floor(y);
    if (erosion[a] && typeof erosion[a][b] === "number") {
      erosion[a][b] += factor / 10;

      if (
        elevations[a] &&
        typeof elevations[a][b] === "number" &&
        elevations[a][b] > -3
      ) {
        elevations[a][b] -= erosion[a][b] / 10000;
      }
      // if (this.getAggradation(a, b) > 0) aggradation[a][b]--;
    }
  }

  getErosion(x, y) {
    const a = Math.floor(x);
    const b = Math.floor(y);
    if (erosion[a] && erosion[a][b]) {
      return erosion[a][b];
    }
    return 0;
  }

  getAggradation(x, y, factor = 1) {
    const a = Math.floor(x);
    const b = Math.floor(y);
    if (aggradation[a] && aggradation[a][b]) {
      return aggradation[a][b];
    }
    if (elevations[a] && typeof elevations[a][b] === "number") {
      elevations[a][b] += factor / 1000;
    }
    return 0;
  }
}

export default TerrainGenerator;
